import { SEO, BreadcrumbSchema, SpeakableSchema, FaqSchema } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, Clock, MapPin, User, MessageSquare, Send, CheckCircle2,
  Shield, Award, Star, ArrowRight
} from 'lucide-react';

/* ─── Hero ─────────────────────────────────────────────── */
const PageHero = () => (
  <section className="relative pt-28 pb-14 bg-navy text-white overflow-hidden">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center max-w-4xl mx-auto">
        <p className="section-eyebrow justify-center">Reach Out</p>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase leading-[1.05]"
          data-speakable
        >
          Get Your <span className="text-lime">Free Estimate</span>
        </h1>
        <p
          className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed font-body"
          data-speakable
        >
          On-site consultation, transparent pricing, and a detailed quote in writing — usually within 24 hours. Call <a href="tel:2016570855" className="text-lime font-semibold hover:underline">(201) 657-0855</a> or send a message below.
        </p>
      </div>
    </div>
  </section>
);

/* ─── Quick-contact strip ──────────────────────────────── */
const QuickContact = () => (
  <section className="bg-lime border-y-[6px] border-navy py-5">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y-2 md:divide-y-0 md:divide-x-2 divide-navy/20 max-w-5xl mx-auto">
        <a href="tel:2016570855" className="group flex items-center justify-center gap-3 py-4 md:py-2 text-navy hover:scale-[1.02] transition-transform">
          <div className="w-10 h-10 bg-navy flex items-center justify-center flex-shrink-0">
            <Phone className="w-5 h-5 text-lime" />
          </div>
          <div className="text-left">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] opacity-80">Call Now</div>
            <div className="font-display font-bold text-lg md:text-xl group-hover:underline">(201) 657-0855</div>
          </div>
        </a>

        <a href="mailto:support@allcompleteconstruction.com" className="group flex items-center justify-center gap-3 py-4 md:py-2 text-navy hover:scale-[1.02] transition-transform">
          <div className="w-10 h-10 bg-navy flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-lime" />
          </div>
          <div className="text-left">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] opacity-80">Email</div>
            <div className="font-display font-bold text-[13px] group-hover:underline break-all">support@allcompleteconstruction.com</div>
          </div>
        </a>

        <div className="flex items-center justify-center gap-3 py-4 md:py-2 text-navy">
          <div className="w-10 h-10 bg-navy flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-lime" />
          </div>
          <div className="text-left">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] opacity-80">Business Hours</div>
            <div className="font-display font-bold text-sm">Mon–Fri 7am–6pm · Sat 8am–4pm</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── Contact form + sidebar ──────────────────────────── */
const ContactFormSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', projectType: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formsubmit.co/ajax/support@allcompleteconstruction.com', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          project_type: formData.projectType,
          message: formData.message,
          _subject: `New Estimate Request from ${formData.name}`,
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
        toast.success('Your request has been sent! We\'ll respond within 24 hours.');
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-compact bg-cream">
      <div className="container mx-auto px-4">
        <div
          ref={ref}
          className="grid lg:grid-cols-5 gap-6 lg:gap-8 max-w-7xl mx-auto"
        >
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3"
          >
            <div className="bg-white border-2 border-navy/10 shadow-hard p-6 md:p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-lime flex items-center justify-center mx-auto mb-5 border-2 border-navy">
                    <CheckCircle2 className="w-8 h-8 text-navy" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-navy mb-2 uppercase tracking-wide">
                    Thanks — We Got It.
                  </h3>
                  <p className="text-stone mb-6 max-w-sm mx-auto font-body">
                    Your request has been received. Expect a call or email from our team within 24 hours.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setIsSubmitted(false)}
                  >
                    Send Another Request
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mb-6 pb-6 border-b-2 border-navy/10">
                    <p className="section-eyebrow">Free Estimate Request</p>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-navy uppercase tracking-wide mb-2">
                      Tell Us About <span className="text-lime">Your Project</span>
                    </h2>
                    <p className="text-stone text-sm font-body">
                      Fill this out and we'll respond within 24 hours with a detailed written quote.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-xs font-display font-bold uppercase tracking-[0.15em] text-navy mb-1.5">
                          Full Name <span className="text-lime">*</span>
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy/40" />
                          <Input
                            id="name"
                            name="name"
                            placeholder="John Smith"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="pl-10 h-11 bg-cream border-2 border-navy/15 rounded-none focus:border-lime focus-visible:ring-0 font-body"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-xs font-display font-bold uppercase tracking-[0.15em] text-navy mb-1.5">
                          Phone Number
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy/40" />
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="(201) 555-0123"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="pl-10 h-11 bg-cream border-2 border-navy/15 rounded-none focus:border-lime focus-visible:ring-0 font-body"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs font-display font-bold uppercase tracking-[0.15em] text-navy mb-1.5">
                        Email Address <span className="text-lime">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy/40" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="pl-10 h-11 bg-cream border-2 border-navy/15 rounded-none focus:border-lime focus-visible:ring-0 font-body"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="projectType" className="block text-xs font-display font-bold uppercase tracking-[0.15em] text-navy mb-1.5">
                        Project Type
                      </label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleInputChange}
                        className="w-full h-11 px-3 bg-cream border-2 border-navy/15 rounded-none focus:border-lime focus:outline-none font-body text-sm text-navy"
                      >
                        <option value="">— Select —</option>
                        <option value="new-build">New Deck Build</option>
                        <option value="replacement">Deck Replacement</option>
                        <option value="repair">Deck Repair</option>
                        <option value="composite">Composite / PVC Install</option>
                        <option value="outdoor-living">Outdoor Living (Pergola, Railings, etc.)</option>
                        <option value="commercial">Commercial Project</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs font-display font-bold uppercase tracking-[0.15em] text-navy mb-1.5">
                        Project Details
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-navy/40" />
                        <Textarea
                          id="message"
                          name="message"
                          placeholder="Tell us about your deck — approximate size, preferred materials, timeline, budget range..."
                          value={formData.message}
                          onChange={handleInputChange}
                          className="pl-10 min-h-[120px] bg-cream border-2 border-navy/15 rounded-none focus:border-lime focus-visible:ring-0 font-body resize-none"
                          rows={4}
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      variant="cta"
                      size="lg"
                      className="w-full h-12"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <motion.span
                            className="block w-4 h-4 border-2 border-navy/30 border-t-navy rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          />
                          Sending...
                        </span>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Get My Free Estimate
                        </>
                      )}
                    </Button>

                    <p className="text-[11px] text-stone/70 text-center font-body">
                      No spam. No pressure. We respond within 24 hours.
                    </p>
                  </form>
                </>
              )}
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-2 space-y-4"
          >
            {/* Why choose us mini-card */}
            <div className="bg-navy text-white border-2 border-navy p-6 shadow-hard">
              <p className="section-eyebrow text-lime" style={{ color: 'hsl(var(--lime))' }}>Why Call Us First</p>
              <h3 className="font-display text-xl font-bold uppercase tracking-wide mb-4">
                25+ Years. <br/><span className="text-lime">One Call.</span>
              </h3>
              <ul className="space-y-2.5">
                {[
                  'Free on-site measurement & design',
                  'Written quote — no hidden fees',
                  'Permit & HOA paperwork handled',
                  'Licensed · Bonded · Insured',
                  'Lifetime workmanship warranty',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-lime flex-shrink-0 mt-0.5" />
                    <span className="text-white/85 text-sm font-body">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trust badges */}
            <div className="bg-white border-2 border-navy/15 p-5">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="py-2">
                  <Shield className="w-6 h-6 text-lime mx-auto mb-1.5" />
                  <div className="font-display text-[10px] font-bold uppercase tracking-wider text-navy leading-tight">Licensed<br/>& Insured</div>
                </div>
                <div className="py-2 border-x-2 border-navy/10">
                  <Award className="w-6 h-6 text-lime mx-auto mb-1.5" />
                  <div className="font-display text-[10px] font-bold uppercase tracking-wider text-navy leading-tight">A+ BBB<br/>Accredited</div>
                </div>
                <div className="py-2">
                  <Star className="w-6 h-6 text-lime mx-auto mb-1.5 fill-lime" />
                  <div className="font-display text-[10px] font-bold uppercase tracking-wider text-navy leading-tight">4.9/5<br/>500+ Reviews</div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="border-2 border-navy/15 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d387191.0361427764!2d-74.82668744831498!3d40.69714942942178!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c0fb959e00409f%3A0x2cd27b07f83f6d8d!2sNew%20Jersey%2C%20USA!5e0!3m2!1sen!2sus!4v1711900000000"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="DeckPros NJ Service Area — Northern & Central New Jersey"
              />
              <div className="bg-navy text-white px-4 py-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-lime flex-shrink-0" />
                <div>
                  <div className="font-display text-[11px] font-bold uppercase tracking-wider text-lime">Service Area</div>
                  <div className="text-xs font-body text-white/80">Morris · Sussex · Warren · Hunterdon · Somerset · All NJ</div>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};

/* ─── Service Areas ──────────────────────────────────── */
const ServiceAreasBlock = () => {
  const counties = [
    { name: 'Morris', path: '/areas/morris-county' },
    { name: 'Sussex', path: '/areas/sussex-county' },
    { name: 'Warren', path: '/areas/warren-county' },
    { name: 'Hunterdon', path: '/areas/hunterdon-county' },
    { name: 'Somerset', path: '/areas/somerset-county' },
  ];

  return (
    <section className="section-snug bg-cream-dark">
      <div className="container mx-auto px-4 max-w-4xl text-center">
        <p className="section-eyebrow justify-center">Local Coverage</p>
        <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-navy mb-3">
          NJ Counties We <span className="text-lime">Serve</span>
        </h2>
        <p className="text-stone font-body mb-6 max-w-xl mx-auto">
          Licensed, bonded, and insured in every county we serve — plus surrounding towns across northern and central NJ.
        </p>

        <div className="flex flex-wrap justify-center gap-2">
          {counties.map((c) => (
            <Link
              key={c.name}
              to={c.path}
              className="flex items-center gap-2 bg-white border-2 border-navy text-navy hover:bg-lime hover:border-navy px-4 py-2.5 font-display uppercase text-xs font-bold tracking-[0.15em] transition-all"
            >
              <MapPin className="w-3 h-3" />
              {c.name} County
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── FAQ ────────────────────────────────────────────── */
const contactFaqs = [
  {
    question: 'How fast will DeckPros NJ respond to my estimate request?',
    answer: 'We respond to every estimate request within 24 hours — usually much sooner during business hours (Mon–Fri 7am–6pm, Sat 8am–4pm). You\'ll receive a phone call or email to schedule a free on-site consultation.',
  },
  {
    question: 'Is the deck estimate really free?',
    answer: 'Yes — 100% free and with no obligation. A DeckPros NJ project consultant visits your home, measures the space, discusses materials and layout, and provides a detailed written quote at no cost to you.',
  },
  {
    question: 'How can I contact DeckPros NJ?',
    answer: 'You can call DeckPros NJ directly at (201) 657-0855, email support@allcompleteconstruction.com, or fill out the estimate form on this page. Business hours are Monday–Friday 7:00 AM – 6:00 PM and Saturday 8:00 AM – 4:00 PM.',
  },
  {
    question: 'What counties does DeckPros NJ serve?',
    answer: 'DeckPros NJ serves Morris, Sussex, Warren, Hunterdon, and Somerset Counties in New Jersey, plus surrounding towns across northern and central NJ. Call (201) 657-0855 to confirm service in your exact town.',
  },
  {
    question: 'Do I need anything ready for my estimate visit?',
    answer: 'Nothing formal is required. Photos or sketches of what you\'re envisioning are helpful but optional. We\'ll bring material samples, measure the space, and discuss layout and budget during the visit.',
  },
  {
    question: 'Can I get a ballpark quote by phone or email?',
    answer: 'For accuracy, DeckPros NJ provides written quotes after an on-site visit. Phone/email ballparks typically miss details that affect the real price. Our on-site visits are free and take about 30–45 minutes.',
  },
];

const FAQSection = () => (
  <section className="section-compact bg-cream">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">Before You Call</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          Contact <span className="text-lime">Questions</span>
        </h2>
      </div>

      <div
        className="max-w-4xl mx-auto space-y-3"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {contactFaqs.map((faq, i) => (
          <details
            key={i}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="group bg-white border-2 border-navy/15 hover:border-lime transition-colors [&[open]]:border-lime [&[open]]:shadow-hard"
          >
            <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
              <h3
                itemProp="name"
                className="font-display text-sm md:text-base font-bold text-navy uppercase tracking-wide group-open:text-lime transition-colors"
              >
                {faq.question}
              </h3>
              <span className="font-display text-2xl text-lime leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
            </summary>
            <div
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
              className="px-5 pb-5 -mt-1"
            >
              <p itemProp="text" className="text-stone text-sm leading-relaxed font-body border-t border-navy/10 pt-4">
                {faq.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

/* ─── ContactPage schema ─────────────────────────────── */
const ContactPageSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact DeckPros NJ',
    url: 'https://deckprosnj.com/contact',
    description: 'Contact DeckPros NJ for a free deck estimate. Call (201) 657-0855, email support@allcompleteconstruction.com, or submit an online form.',
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'DeckPros NJ',
      telephone: '+1-201-657-0855',
      email: 'support@allcompleteconstruction.com',
      address: { '@type': 'PostalAddress', addressRegion: 'NJ', addressCountry: 'US' },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+1-201-657-0855',
          contactType: 'sales',
          areaServed: 'US-NJ',
          availableLanguage: ['English'],
          contactOption: 'TollFree',
          hoursAvailable: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '07:00', closes: '18:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '08:00', closes: '16:00' },
          ],
        },
        {
          '@type': 'ContactPoint',
          email: 'support@allcompleteconstruction.com',
          contactType: 'customer support',
          areaServed: 'US-NJ',
          availableLanguage: ['English'],
        },
      ],
    },
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

/* ─── Page ─────────────────────────────────────────── */
const Contact = () => {
  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Contact Us · Free Deck Estimate"
        description="Contact DeckPros NJ for a free deck estimate. Call (201) 657-0855, email support@allcompleteconstruction.com, or submit the form. Serving Morris, Sussex, Warren, Hunterdon & Somerset Counties, NJ. 24-hour response."
        canonical="/contact"
        keywords="contact DeckPros NJ, deck estimate NJ, free deck quote, New Jersey deck contact, Morris County deck estimate, deck builder phone number"
      />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Contact', url: '/contact' }]} />
      <SpeakableSchema />
      <FaqSchema faqs={contactFaqs} />
      <ContactPageSchema />

      <Header />
      <main>
        <PageHero />
        <QuickContact />
        <ContactFormSection />
        <ServiceAreasBlock />
        <FAQSection />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
